import FindLocation from "../models/FindLocation";
import { DefaultModel } from "../models/IModel";
import Location, { LocationDetail } from "../models/Location";
import { PriceDetail } from "../models/Order";
import { PaymentMethodList } from "../models/User";
import http from "./http";

export type RevenueCurrencyBreakdown = {
	currencyCode: string;
	totalCommission: number;
	bookingCount: number;
	totalQuantity: number;
	totalDays: number;
};

export type AdminOrderRevenueSummary = {
	bookingCount: number;
	totalQuantity: number;
	totalCommission: number | null;
	currencyCode: string | null;
	currencyBreakdown: RevenueCurrencyBreakdown[];
};

export const estimateFee = async (
	fee: number,
	quantity: number,
	timeIn: string,
	timeOut: string,
	insuranceFee: number | null | undefined,
	timezoneId: string
) => {
	let params: any = {
		fee: fee,
		quantity: quantity,
		timeIn: timeIn,
		timeOut: timeOut,
		insuranceFee: insuranceFee,
		timeZoneId: timezoneId,
	};

	const { data } = await http.get(`/order-estimate-fee?${new URLSearchParams(params).toString()}`, {});
	return data;
};

export const OrderGuest = async (
	partnerId: number,
	timeIn?: string,
	timeOut?: string,
	quantity?: number,
	paymentMethod?: number,
	totalMoney?: number,
	phone?: string,
	communicationId?: string,
	fullName?: string,
	email?: string,
	selectedCountryCode?: string,
	PaymentId?: number,
	paymentMethodText?: PaymentMethodList
) => {
	let { data } = await http.post(`/order-guest`, {
		partnerId: partnerId,
		timeIn: timeIn,
		timeOut: timeOut,
		quantity: quantity,
		paymentMethod: paymentMethod,
		totalMoney: totalMoney,
		phone: phone,
		communicationId: communicationId,
		fullName: fullName,
		email: email,
		phonePrefix: selectedCountryCode,
		subPaymentMethod: PaymentId,
		paymentMethodSelect: paymentMethodText,
	});

	return data;
};

export const OrderGuestLogin = async (
	partnerId: number,
	timeIn?: string,
	timeOut?: string,
	quantity?: number,
	paymentMethod?: number,
	totalMoney?: number,
	PaymentId?: number,
	paymentMethodText?: PaymentMethodList
) => {
	let { data } = await http.post(`/order`, {
		partnerId: partnerId,
		timeIn: timeIn,
		timeOut: timeOut,
		quantity: quantity,
		paymentMethod: paymentMethod,
		totalMoney: totalMoney,
		subPaymentMethod: PaymentId,
		paymentMethodSelect: paymentMethodText,
	});

	return data;
};

export const OnePayPaymentSuccess = async (OrderId: string, forGuest: boolean) => {
	let { data } = await http.post(`/onepay-payment-success`, {
		forGuest: forGuest,
		orderId: OrderId,
	});
	return data;
};

export const createdPass = async (code: string, newPassword: string, confirmPassword: string, userId: number) => {
	let { data } = await http.put(`/update-password-guest`, {
		code: code,
		newPassword: newPassword,
		confirmPassword: confirmPassword,
		userId: userId,
	});
	return data;
};

export async function getListOrder(
	inPage: number,
	inPerPage: number
): Promise<{
	data: PriceDetail[];
	count: number;
	numberPage: number;
}> {
	let results = await http.get(`/my-order?perPage=${inPerPage || 20}&page=${inPage || 1}`);
	return {
		data: DefaultModel.parseList<PriceDetail>(results.data.docs, () => new PriceDetail()),
		count: results.data.total,
		numberPage: results.data.pages,
	};
}
export async function getListOrderPartner(
	inPage: number,
	inPerPage: number
): Promise<{
	data: PriceDetail[];
	count: number;
	numberPage: number;
}> {
	let results = await http.get(`/order-by-partner?perPage=${inPerPage || 20}&page=${inPage || 1}`);
	return {
		data: DefaultModel.parseList<PriceDetail>(results.data.docs, () => new PriceDetail()),
		count: results.data.total,
		numberPage: results.data.pages,
	};
}
export async function getListOrderAdmin(
	inPage: number,
	inPerPage: number,
	status?: string[],
	fromDate?: Date,
	toDate?: Date,
	store?: string,
	userId?: number,
	fromReplyDate?: Date,
	toReplyDate?: Date,
	country?: string,
	provice?: string,
	id?: string | null,
	stationActive?: string | null
): Promise<{
	data: PriceDetail[];
	count: number;
	numberPage: number;
}> {
	const params: any = {
		...(status && { multipleStatus: status.join(";") }),
		...(fromDate && { timeIn: `${fromDate.getTime()}` }),
		...(toDate && { timeOut: `${toDate.getTime()}` }),
		...(store && { partnerId: store }),
		...(userId && { customerUserId: userId }),
		...(fromReplyDate && { fromPickupDate: `${fromReplyDate.getTime()}` }),
		...(toReplyDate && { toPickupDate: `${toReplyDate.getTime()}` }),
		...(country && { country }),
		...(provice && { destinationId: provice }),
		...(id && { orderId: id }),
		...(stationActive && { partnerActive: stationActive }),
	};
	let results = await http.get(
		`/order-by-admin?perPage=${inPerPage || 20}&page=${inPage || 1}&${new URLSearchParams(params).toString()}`
	);
	return {
		data: DefaultModel.parseList<PriceDetail>(results.data.docs, () => new PriceDetail()),
		count: results.data.total,
		numberPage: results.data.pages,
	};
}

export async function getAdminOrderRevenueSummary(params?: {
	status?: string[];
	fromDate?: Date;
	toDate?: Date;
	store?: string;
	userId?: number;
	fromReplyDate?: Date;
	toReplyDate?: Date;
	country?: string;
	provice?: string;
	id?: string | null;
	stationActive?: string | null;
}): Promise<AdminOrderRevenueSummary> {
	const queryParams: any = {
		...(params?.status?.length && { multipleStatus: params.status.join(";") }),
		...(params?.fromDate && { timeIn: `${params.fromDate.getTime()}` }),
		...(params?.toDate && { timeOut: `${params.toDate.getTime()}` }),
		...(params?.store && { partnerId: params.store }),
		...(params?.userId && { customerUserId: params.userId }),
		...(params?.fromReplyDate && { fromPickupDate: `${params.fromReplyDate.getTime()}` }),
		...(params?.toReplyDate && { toPickupDate: `${params.toReplyDate.getTime()}` }),
		...(params?.country && { country: params.country }),
		...(params?.provice && { destinationId: params.provice }),
		...(params?.id && { orderId: params.id }),
		...(params?.stationActive && { partnerActive: params.stationActive }),
	};

	const { data } = await http.get(`/admin/orders/revenue-summary`, {
		params: queryParams,
	});

	return {
		bookingCount: Number(data?.bookingCount) || 0,
		totalQuantity: Number(data?.totalQuantity) || 0,
		totalCommission:
			data?.totalCommission === null || data?.totalCommission === undefined
				? null
				: Number(data.totalCommission) || 0,
		currencyCode: data?.currencyCode || null,
		currencyBreakdown: Array.isArray(data?.currencyBreakdown)
			? data.currencyBreakdown.map((item: any) => ({
					currencyCode: `${item?.currencyCode || "USD"}`,
					totalCommission: Number(item?.totalCommission) || 0,
					bookingCount: Number(item?.bookingCount) || 0,
					totalQuantity: Number(item?.totalQuantity) || 0,
					totalDays: Number(item?.totalDays) || 0,
			  }))
			: [],
	};
}

export const PostOrderAdmin = async (
	partnerId: number,
	timeOut: string,
	timeIn: string,
	quantity: string,
	paymentMethod: string,
	totalMoney: string,
	customerUserId: number
) => {
	let { data } = await http.post(`/order-by-admin`, {
		partnerId: partnerId,
		timeIn: timeIn,
		timeOut: timeOut,
		quantity: quantity,
		paymentMethod: paymentMethod,
		totalMoney: totalMoney,
		customerUserId: customerUserId,
	});
	return data;
};

export const CancelOrder = async (id: number) => {
	let { data } = await http.post(`/cancel-order/${id}`);
	return data;
};

export const CancelOrderByAdmin = async (id: number) => {
	let { data } = await http.post(`/cancel-order-by-admin/${id}`);
	return data;
};

export const CancelOrderByAdminNoRefund = async (id: number) => {
	let { data } = await http.post(`/cancel-order-by-admin-no-refund/${id}`);
	return data;
};

/** Admin "Cancel booking…" (16/10/2026) — refund through the original gateway (needs orders.refund). */
export const CancelOrderByAdminWithRefund = async (id: number) => {
	let { data } = await http.post(`/admin/orders/${id}/cancel-with-refund`);
	return data;
};

/** Nhãn "Disputed" cho một trang đơn (17/10/2026). Lỗi ⇒ trả rỗng: không có nhãn chứ không vỡ trang. */
export const getAdminOrderDisputes = async (orderIds: number[]) => {
	const ids = orderIds.filter((n) => Number.isFinite(n) && n > 0).slice(0, 200);
	if (!ids.length) return {} as Record<string, any>;
	try {
		let { data } = await http.get(`/admin/order-disputes`, { params: { orderIds: ids.join(",") } });
		return (data?.overlays || {}) as Record<string, any>;
	} catch (e) {
		return {} as Record<string, any>;
	}
};

/** Refund a booking already "Cancelled · No refund" (needs orders.refund). */
export const RefundCancelledOrderByAdmin = async (id: number) => {
	let { data } = await http.post(`/admin/orders/${id}/refund-cancelled`);
	return data;
};

/** Admin "Reopen booking…" (26/10/2026) — returns the status the booking landed in (Reserved / No-Show / Confirmed). */
export const ReopenCancelledOrderByAdmin = async (id: number, note: string): Promise<{ status: number }> => {
	let { data } = await http.post(`/admin/orders/${id}/reopen`, { note });
	return data;
};

/** Chargeback thua mà đơn đã trả trạm: sau khi đã trừ trạm (Balance adjustment ghi "#<mã đơn>") ⇒ chốt Cancelled · Refunded. */
export const SettleChargebackAdmin = async (id: number) => {
	let { data } = await http.post(`/admin/orders/${id}/chargeback-settled`);
	return data;
};

export type CancelNoRefundReason = "no_show" | "late_cancellation" | "duplicate" | "dispute_lost" | "other";

/** Admin "Cancel booking…" — keep the payment; reason required (note required for "other"). */
export const CancelOrderByAdminWithoutRefund = async (id: number, reason: CancelNoRefundReason, note?: string) => {
	let { data } = await http.post(`/admin/orders/${id}/cancel-no-refund`, { reason, note });
	return data;
};

export const PutOrderAdmin = async (
	orderId: number,
	partnerId: number | undefined,
	timeOut: string,
	timeIn: string,
	quantity: string,
	paymentMethod: string,
	totalMoney: string,
	status: string,
	customerUserId: number,
	allowPartnerId: boolean,
	pastDateReason?: string
) => {
	let { data } = await http.put(`/order/${orderId}`, {
		partnerId: partnerId,
		timeIn: timeIn,
		timeOut: timeOut,
		quantity: quantity,
		paymentMethod: paymentMethod,
		totalMoney: totalMoney,
		status: status,
		customerUserId: customerUserId,
		allowQuantity: true,
		allowTotalMoney: true,
		allowStatus: true,
		allowTimeIn: true,
		allowTimeOut: true,
		allowPartnerId: partnerId ? allowPartnerId : false,
		...(pastDateReason ? { pastDateReason } : {}),
	});
	return data;
};

export const FinishOrderByAdmin = async (id: number) => {
	let { data } = await http.post(`/finish-order-by-admin/${id}`);
	return data;
};

export const PaidOrderByAdmin = async (id: number) => {
	let { data } = await http.post(`/paid-order-by-admin/${id}`);
	return data;
};

export const BulkSendBookingEmailByAdmin = async (orderIds: number[]) => {
	let { data } = await http.post(`/admin/orders/bulk-send-email`, {
		orderIds,
	});
	return data;
};

/** Admin override for station arrival / no-show (VerificationStatus: 1 = Verified, 2 = No-Show) */
export const adminVerifyOrderOverride = async (orderId: number, verificationStatus: number) => {
	const { data } = await http.post(`/admin/verify-order-override/${orderId}`, {
		verificationStatus,
	});
	return data;
};

export async function exportOrdersCsv(
	status?: string[],
	fromDate?: Date | null,
	toDate?: Date | null,
	store?: string | null,
	userId?: number | null,
	fromReplyDate?: Date | null,
	toReplyDate?: Date | null,
	country?: string | null,
	provice?: string | null,
	id?: string | null,
	stationActive?: string | null
): Promise<Blob> {
	const params: any = {
		...(status && status.length > 0 && { multipleStatus: status.join(";") }),
		...(fromDate && { timeIn: `${fromDate.getTime()}` }),
		...(toDate && { timeOut: `${toDate.getTime()}` }),
		...(store && { partnerId: store }),
		...(userId && { customerUserId: userId }),
		...(fromReplyDate && { fromPickupDate: `${fromReplyDate.getTime()}` }),
		...(toReplyDate && { toPickupDate: `${toReplyDate.getTime()}` }),
		...(country && { country }),
		...(provice && { destinationId: provice }),
		...(id && { orderId: id }),
		...(stationActive && { partnerActive: stationActive }),
	};

	const res = await http.get(`/order-by-admin/export?${new URLSearchParams(params).toString()}`, {
		responseType: "blob",
	});
	return res.data as Blob;
}

export const OrderStripePaymentStart = async (orderId: string, clientSecretId: string, paymentMethodType?: string) => {
	let { data } = await http.post(`/order-stripe-payment-start`, {
		orderId: orderId,
		clientSecretId: clientSecretId,
		...(paymentMethodType && { paymentMethodSelect: paymentMethodType }),
	});
	return data;
};

/** Link thanh toán đặt sẵn (Admin > Orders > "Payment link"): máy chủ mã hoá trạm/số túi/ngày/liên hệ khách
 *  thành một mã cho /order?p=<mã>, để liên hệ khách không nằm trần trên URL. Không tạo đơn. */
export const createOrderPrefillLink = async (body: {
	partnerId: number;
	quantity: number;
	dropOff: string;
	pickUp: string;
	fullName?: string;
	email?: string;
	phone?: string;
	phonePrefix?: string;
	sourceOrderId?: number | null;
}): Promise<{ token: string; expiresAt: string }> => {
	const { data } = await http.post(`/admin/order-prefill-link`, body);
	return data;
};

/** Đọc đơn cũ để điền sẵn hộp "Payment link" (cùng API màn chi tiết đơn của Admin, quyền orders.view). */
export const getOrderForPrefill = async (id: number) => {
	const { data } = await http.get(`/order-detail-by-admin/${id}`);
	const o = data?.data ?? data;
	return {
		id: Number(o?.id),
		partnerId: Number(o?.partnerId),
		partnerLabel: [o?.partner?.name, o?.partner?.displayName].filter(Boolean).join(" · "),
		quantity: Number(o?.quantity) || 1,
		dropOff: String(o?.timeInString || o?.timeIn || "").slice(0, 10),
		pickUp: String(o?.timeOutString || o?.timeOut || "").slice(0, 10),
		fullName: String(o?.customer?.fullName || ""),
		email: String(o?.customer?.email || o?.email || ""),
		phone: String(o?.customer?.phone || o?.phone || ""),
		phonePrefix: String(o?.customer?.phonePrefix || ""),
	};
};
